import React, { useContext, useEffect, useRef, useState } from 'react'
import fetchAllCategories from '../../helpers/fetchAllCategories';
import { displayNGNCurrency } from '../../helpers/displayCurrency';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6';
import addToCart from '../../helpers/addToCart';
import Context from '../../context';
import { Link } from 'react-router-dom';
import scrollTop from '../../helpers/scrollTop';
import { ShoppingCart, Star, Heart } from 'lucide-react';
import { summaryApi } from '../../common';

function CategoryProductDisplay({ productsCategory, heading }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [scroll, setScroll] = useState(0);
    const [hoveredId, setHoveredId] = useState(null);
    const [productRatings, setProductRatings] = useState({});
    const [wishlist, setWishlist] = useState({});

    const { fetchUserAddToCart } = useContext(Context);
    const scrollElement = useRef();

    // Elegant color palette matching ProductDetails
    const colors = {
        primary: '#1a365d', // Elegant dark blue
        accent: '#7c3aed', // Elegant purple
        bg: '#fafafa', // Light background
        bgSecondary: '#ffffff', // White background
        bgTertiary: '#f5f5f5', // Light gray background
        text: '#171717', // Dark text
        textSecondary: '#525252', // Medium gray text
        border: '#d4d4d4', // Light border
        shadow: 'rgba(0, 0, 0, 0.05)',
        success: '#10b981'
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchAllCategories(productsCategory);

            if (response && response.data) {
                setData(response.data);
                // Fetch ratings for each product
                response.data.forEach(product => {
                    fetchProductRating(product._id);
                });
            } else {
                setError("No data found");
            }
        } catch (error) {
            console.error(error);
            setError(error.message || "An error occurred");
        }
        setLoading(false);
    }

    const fetchProductRating = async (productId) => {
        try {
            const response = await fetch(`${summaryApi.getReviews.url}/${productId}`, {
                method: summaryApi.getReviews.method,
                credentials: 'include'
            });
            const data = await response.json();
            
            if (data.data && data.data.length > 0) {
                const totalRating = data.data.reduce((sum, review) => sum + review.rating, 0);
                const averageRating = totalRating / data.data.length;
                
                setProductRatings(prev => ({
                    ...prev,
                    [productId]: {
                        average: averageRating,
                        count: data.data.length
                    }
                }));
            }
        } catch (error) {
            console.error(`Error fetching rating for product ${productId}:`, error);
        }
    }

    useEffect(() => {
        fetchData();
    }, [productsCategory])

    const scrollRight = () => {
        if (scrollElement.current) {
            const newScrollPosition = scroll + 400;
            scrollElement.current.scrollTo({
                left: newScrollPosition,
                behavior: "smooth"
            })
            setScroll(newScrollPosition)
        }
    }

    const scrollLeft = () => {
        if (scrollElement.current) {
            const newScrollPosition = scroll - 400;
            scrollElement.current.scrollTo({
                left: newScrollPosition,
                behavior: "smooth"
            })
            setScroll(newScrollPosition)
        }
    }

    const handleAddToCart = async(e, productId) => {
       await addToCart(e, productId, fetchUserAddToCart);
    }

    const toggleWishlist = (productId) => {
        setWishlist(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    }

    const renderStars = (rating) => {
        return [...Array(5)].map((_, i) => (
            <Star
                key={i}
                size={14}
                fill={i < Math.floor(rating) ? colors.primary : colors.border}
                style={{ color: i < Math.floor(rating) ? colors.primary : colors.border }}
            />
        ));
    }

    const SkeletonLoader = () => {
        return Array(5).fill().map((_, index) => (
            <div 
                key={index} 
                className='min-w-[260px] md:min-w-[300px] rounded-lg overflow-hidden animate-pulse'
                style={{ backgroundColor: colors.bgSecondary }}
            >
                <div 
                    className='w-full h-56'
                    style={{ backgroundColor: colors.bgTertiary }}
                />
                <div className='p-4 space-y-3'>
                    <div 
                        className='h-4 rounded w-3/4'
                        style={{ backgroundColor: colors.bgTertiary }}
                    />
                    <div 
                        className='h-3 rounded w-1/2'
                        style={{ backgroundColor: colors.bgTertiary }}
                    />
                    <div 
                        className='h-6 rounded w-2/3'
                        style={{ backgroundColor: colors.bgTertiary }}
                    />
                </div>
            </div>
        ));
    }

    return (
        <div className='relative group py-6' style={{ fontFamily: 'Georgia, serif' }}>
            {/* Section Header */}
            <div className='mb-8'>
                <h2 
                    className='text-3xl font-normal mb-3 tracking-tight text-neutral-900'
                    style={{ fontFamily: 'Georgia, serif', fontWeight: 400 }}
                >
                    {heading}
                </h2>
                <div 
                    className='h-px w-16 bg-neutral-300'
                />
            </div>

            {/* Scroll Navigation Buttons */}
            <button
                onClick={scrollLeft}
                className='absolute -left-5 md:left-0 top-1/2 transform -translate-y-1/2 z-20 p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 hidden md:flex items-center justify-center bg-neutral-900 text-neutral-200'
                style={{
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    fontFamily: 'Georgia, serif'
                }}
                title="Scroll left"
            >
                <FaAngleLeft className='text-xl' />
            </button>

            <button
                onClick={scrollRight}
                className='absolute -right-5 md:right-0 top-1/2 transform -translate-y-1/2 z-20 p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 hidden md:flex items-center justify-center bg-neutral-900 text-neutral-200'
                style={{
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    fontFamily: 'Georgia, serif'
                }}
                title="Scroll right"
            >
                <FaAngleRight className='text-xl' />
            </button>

            {/* Products Container */}
            <div 
                className='flex items-center gap-6 overflow-x-auto scrollbar-none scroll-smooth px-2'
                ref={scrollElement}
            >
                {loading ? (
                    <SkeletonLoader />
                ) : error ? (
                    <div 
                        className='text-center py-8 w-full text-neutral-600'
                        style={{ fontFamily: 'Georgia, serif' }}
                    >
                        {error}
                    </div>
                ) : data.length === 0 ? (
                    <div 
                        className='text-center py-8 w-full text-neutral-600'
                        style={{ fontFamily: 'Georgia, serif' }}
                    >
                        No products found
                    </div>
                ) : (
                    data.map((product) => (
                        <div
                            key={product._id}
                            className='min-w-[260px] md:min-w-[300px] group/card transition-all duration-300'
                            onMouseEnter={() => setHoveredId(product._id)}
                            onMouseLeave={() => setHoveredId(null)}
                        >
                            {/* Product Card */}
                            <Link 
                                to={"/product/" + product?._id}
                                className='block h-full rounded-lg overflow-hidden border border-neutral-300 hover:shadow-lg transition-all duration-300 bg-white'
                                onClick={scrollTop}
                            >
                                {/* Image Container */}
                                <div 
                                    className='relative w-full h-56 overflow-hidden bg-neutral-100'
                                >
                                    {/* Product Image */}
                                    <img
                                        src={product.productImage[0]}
                                        alt={product.productName}
                                        className='w-full h-full object-scale-down p-4 group-hover/card:scale-110 transition-transform duration-300'
                                    />

                                    {/* Discount Badge */}
                                    {product.productDiscount && product.productPrice && (
                                        <div
                                            className='absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium tracking-wide bg-neutral-900 text-neutral-200'
                                            style={{ fontFamily: 'Georgia, serif' }}
                                        >
                                            -{Math.round((product.productDiscount / product.productPrice) * 100)}%
                                        </div>
                                    )}

                                    {/* Wishlist Button */}
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleWishlist(product._id);
                                        }}
                                        className='absolute top-3 left-3 p-2 rounded-full transition-all duration-300 opacity-0 group-hover/card:opacity-100 hover:scale-110 bg-white border-2 border-red-700'
                                        style={{ 
                                            color: wishlist[product._id] ? 'white' : '#b91c1c',
                                            backgroundColor: wishlist[product._id] ? '#b91c1c' : 'white',
                                            fontFamily: 'Georgia, serif'
                                        }}
                                        title="Add to wishlist"
                                    >
                                        <Heart 
                                            size={16}
                                            fill={wishlist[product._id] ? 'currentColor' : 'none'}
                                        />
                                    </button>

                                    {/* Overlay Gradient */}
                                    <div 
                                        className='absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300'
                                        style={{
                                            background: 'linear-gradient(to top, rgba(26, 54, 93, 0.1), transparent)'
                                        }}
                                    />
                                </div>

                                {/* Content Container */}
                                <div className='p-4 flex flex-col gap-3'>
                                    {/* Product Name */}
                                    <h3 
                                        className='font-normal text-sm line-clamp-2 text-neutral-900 tracking-tight'
                                        style={{ fontFamily: 'Georgia, serif', fontWeight: 400 }}
                                    >
                                        {product.productName}
                                    </h3>

                                    {/* Category */}
                                    <p 
                                        className='text-xs capitalize text-neutral-600'
                                        style={{ fontFamily: 'Georgia, serif' }}
                                    >
                                        {product.productsCategory}
                                    </p>

                                    {/* Rating - REAL DATA */}
                                    <div className='flex items-center gap-1'>
                                        <div className='flex gap-0.5'>
                                            {productRatings[product._id] ? (
                                                renderStars(productRatings[product._id].average)
                                            ) : (
                                                [...Array(5)].map((_, i) => (
                                                    <Star 
                                                        key={i} 
                                                        size={14} 
                                                        style={{ color: colors.border }} 
                                                    />
                                                ))
                                            )}
                                        </div>
                                        <span 
                                            className='text-xs ml-1 text-neutral-600'
                                            style={{ fontFamily: 'Georgia, serif' }}
                                        >
                                            {productRatings[product._id] ? (
                                                `(${productRatings[product._id].average.toFixed(1)})`
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </div>

                                    {/* Price Section */}
                                    <div className='flex items-center gap-2 flex-wrap mt-2'>
                                        <span 
                                            className='font-normal text-lg text-neutral-900'
                                            style={{ fontFamily: 'Georgia, serif', fontWeight: 400 }}
                                        >
                                            {displayNGNCurrency(product.productPrice)}
                                        </span>
                                        {product.productDiscount && (
                                            <span 
                                                className='text-xs line-through text-neutral-500'
                                                style={{ fontFamily: 'Georgia, serif' }}
                                            >
                                                {displayNGNCurrency(product.productDiscount)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Add to Cart Button */}
                                    <button
                                        onClick={(e) => handleAddToCart(e, product?._id)}
                                        className='w-full py-2.5 px-4 rounded-lg font-medium text-xs uppercase tracking-wider transition-all duration-300 hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2 border border-neutral-900 text-neutral-200 bg-neutral-900'
                                        style={{ fontFamily: 'Georgia, serif' }}
                                    >
                                        <ShoppingCart size={16} />
                                        Add to cart
                                    </button>
                                </div>
                            </Link>
                        </div>
                    ))
                )}
            </div>

            <style jsx>{`
                .scrollbar-none::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-none {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    )
}

export default CategoryProductDisplay;






// import React, { useContext, useEffect, useRef, useState } from 'react'
// import fetchAllCategories from '../../helpers/fetchAllCategories';
// import { displayNGNCurrency } from '../../helpers/displayCurrency';
// import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6';
// import addToCart from '../../helpers/addToCart';
// import Context from '../../context';
// import { Link } from 'react-router-dom';
// import scrollTop from '../../helpers/scrollTop';
// import { useTheme } from '../../components/Theme/ThemeProvider';
// import { ShoppingCart, Star, Heart } from 'lucide-react';
// import { summaryApi } from '../../common';

// function CategoryProductDisplay({ productsCategory, heading }) {
//     const { colors } = useTheme();
//     const [data, setData] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState(null);
//     const [scroll, setScroll] = useState(0);
//     const [hoveredId, setHoveredId] = useState(null);
//     const [productRatings, setProductRatings] = useState({});
//     const [wishlist, setWishlist] = useState({});

//     const { fetchUserAddToCart } = useContext(Context);
//     const scrollElement = useRef();

//     const fetchData = async () => {
//         setLoading(true);
//         setError(null);
//         try {
//             const response = await fetchAllCategories(productsCategory);

//             if (response && response.data) {
//                 setData(response.data);
//                 response.data.forEach(product => {
//                     fetchProductRating(product._id);
//                 });
//             } else {
//                 setError("No data found");
//             }
//         } catch (error) {
//             console.error(error);
//             setError(error.message || "An error occurred");
//         }
//         setLoading(false);
//     }

//     const fetchProductRating = async (productId) => {
//         try {
//             const response = await fetch(`${summaryApi.getReviews.url}/${productId}`, {
//                 method: summaryApi.getReviews.method,
//                 credentials: 'include'
//             });
//             const data = await response.json();
            
//             if (data.data && data.data.length > 0) {
//                 const totalRating = data.data.reduce((sum, review) => sum + review.rating, 0);
//                 const averageRating = totalRating / data.data.length;
                
//                 setProductRatings(prev => ({
//                     ...prev,
//                     [productId]: {
//                         average: averageRating,
//                         count: data.data.length
//                     }
//                 }));
//             }
//         } catch (error) {
//             console.error(`Error fetching rating for product ${productId}:`, error);
//         }
//     }

//     useEffect(() => {
//         fetchData();
//     }, [productsCategory])

//     const scrollRight = () => {
//         if (scrollElement.current) {
//             const newScrollPosition = scroll + 400;
//             scrollElement.current.scrollTo({
//                 left: newScrollPosition,
//                 behavior: "smooth"
//             })
//             setScroll(newScrollPosition)
//         }
//     }

//     const scrollLeft = () => {
//         if (scrollElement.current) {
//             const newScrollPosition = scroll - 400;
//             scrollElement.current.scrollTo({
//                 left: newScrollPosition,
//                 behavior: "smooth"
//             })
//             setScroll(newScrollPosition)
//         }
//     }

//     const handleAddToCart = async(e, productId) => {
//        await addToCart(e, productId, fetchUserAddToCart);
//     }

//     const toggleWishlist = (productId) => {
//         setWishlist(prev => ({
//             ...prev,
//             [productId]: !prev[productId]
//         }));
//     }

//     const renderStars = (rating) => {
//         return [...Array(5)].map((_, i) => (
//             <Star
//                 key={i}
//                 size={13}
//                 fill={i < Math.floor(rating) ? '#1a365d' : '#d4d4d4'}
//                 style={{ color: i < Math.floor(rating) ? '#1a365d' : '#d4d4d4' }}
//             />
//         ));
//     }

//     const SkeletonLoader = () => {
//         return Array(5).fill().map((_, index) => (
//             <div 
//                 key={index} 
//                 className='min-w-[260px] md:min-w-[300px] overflow-hidden animate-pulse bg-neutral-100 border border-neutral-200'
//             >
//                 <div 
//                     className='w-full h-56 bg-neutral-200'
//                 />
//                 <div className='p-4 space-y-3'>
//                     <div 
//                         className='h-4 w-3/4 bg-neutral-200'
//                     />
//                     <div 
//                         className='h-3 w-1/2 bg-neutral-200'
//                     />
//                     <div 
//                         className='h-6 w-2/3 bg-neutral-200'
//                     />
//                 </div>
//             </div>
//         ));
//     }

//     return (
//         <div className='relative group py-6' style={{ fontFamily: 'Georgia, serif' }}>
//             {/* Section Header */}
//             <div className='mb-8'>
//                 <h2 
//                     className='text-2xl font-normal mb-3 text-neutral-900 tracking-tight'
//                     style={{ fontFamily: 'Georgia, serif', fontWeight: 400 }}
//                 >
//                     {heading}
//                 </h2>
//                 <div 
//                     className='h-0.5 w-12 bg-slate-800'
//                 />
//             </div>

//             {/* Scroll Navigation Buttons */}
//             <button
//                 onClick={scrollLeft}
//                 className='absolute -left-5 md:left-0 top-20 z-20 p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-[1.02] hidden md:flex items-center justify-center bg-slate-800 text-white'
//                 title="Scroll left"
//             >
//                 <FaAngleLeft className='text-lg' />
//             </button>

//             <button
//                 onClick={scrollRight}
//                 className='absolute -right-5 md:right-0 top-20 z-20 p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-[1.02] hidden md:flex items-center justify-center bg-slate-800 text-white'
//                 title="Scroll right"
//             >
//                 <FaAngleRight className='text-lg' />
//             </button>

//             {/* Products Container */}
//             <div 
//                 className='flex items-center gap-6 overflow-x-auto scrollbar-none scroll-smooth px-2'
//                 ref={scrollElement}
//             >
//                 {loading ? (
//                     <SkeletonLoader />
//                 ) : error ? (
//                     <div 
//                         className='text-center py-8 w-full text-neutral-600'
//                         style={{ fontFamily: 'Georgia, serif' }}
//                     >
//                         {error}
//                     </div>
//                 ) : data.length === 0 ? (
//                     <div 
//                         className='text-center py-8 w-full text-neutral-600'
//                         style={{ fontFamily: 'Georgia, serif' }}
//                     >
//                         No products found
//                     </div>
//                 ) : (
//                     data.map((product) => (
//                         <div
//                             key={product._id}
//                             className='min-w-[260px] md:min-w-[300px] group/card transition-all duration-300'
//                             onMouseEnter={() => setHoveredId(product._id)}
//                             onMouseLeave={() => setHoveredId(null)}
//                         >
//                             {/* Product Card */}
//                             <Link 
//                                 to={"/product/" + product?._id}
//                                 className='block h-full overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-neutral-100 border border-neutral-300'
//                                 onClick={scrollTop}
//                             >
//                                 {/* Image Container */}
//                                 <div 
//                                     className='relative w-full h-56 overflow-hidden bg-neutral-50'
//                                 >
//                                     {/* Product Image */}
//                                     <img
//                                         src={product.productImage[0]}
//                                         alt={product.productName}
//                                         className='w-full h-full object-scale-down p-4 group-hover/card:scale-110 transition-transform duration-300'
//                                     />

//                                     {/* Discount Badge */}
//                                     {product.productDiscount && product.productPrice && (
//                                         <div
//                                             className='absolute top-3 right-3 px-3 py-1 text-[10px] font-medium tracking-wider uppercase text-white bg-red-700'
//                                             style={{ fontFamily: 'Georgia, serif' }}
//                                         >
//                                             -{Math.round((product.productDiscount / product.productPrice) * 100)}%
//                                         </div>
//                                     )}

//                                     {/* Wishlist Button */}
//                                     <button
//                                         onClick={(e) => {
//                                             e.preventDefault();
//                                             toggleWishlist(product._id);
//                                         }}
//                                         className='absolute top-3 left-3 p-2 transition-all duration-300 opacity-0 group-hover/card:opacity-100 hover:scale-110 border-2 border-red-700'
//                                         style={{
//                                             backgroundColor: wishlist[product._id] ? '#b91c1c' : '#fafafa',
//                                             color: wishlist[product._id] ? 'white' : '#171717'
//                                         }}
//                                         title="Add to wishlist"
//                                     >
//                                         <Heart 
//                                             size={15}
//                                             fill={wishlist[product._id] ? 'currentColor' : 'none'}
//                                         />
//                                     </button>
//                                 </div>

//                                 {/* Content Container */}
//                                 <div className='p-4 flex flex-col gap-3'>
//                                     {/* Product Name */}
//                                     <h3 
//                                         className='font-normal text-sm line-clamp-2 transition-colors duration-300 text-neutral-900 tracking-tight'
//                                         style={{ fontFamily: 'Georgia, serif', fontWeight: 400 }}
//                                     >
//                                         {product.productName}
//                                     </h3>

//                                     {/* Category */}
//                                     <p 
//                                         className='text-[11px] capitalize text-neutral-600 tracking-wide'
//                                         style={{ fontFamily: 'Georgia, serif' }}
//                                     >
//                                         {product.productsCategory}
//                                     </p>

//                                     {/* Rating */}
//                                     <div className='flex items-center gap-1'>
//                                         <div className='flex gap-0.5'>
//                                             {productRatings[product._id] ? (
//                                                 renderStars(productRatings[product._id].average)
//                                             ) : (
//                                                 [...Array(5)].map((_, i) => (
//                                                     <Star key={i} size={13} style={{ color: '#d4d4d4' }} />
//                                                 ))
//                                             )}
//                                         </div>
//                                         <span 
//                                             className='text-[11px] ml-1 text-neutral-600'
//                                             style={{ fontFamily: 'Georgia, serif' }}
//                                         >
//                                             {productRatings[product._id] ? (
//                                                 `(${productRatings[product._id].average.toFixed(1)}/5)`
//                                             ) : (
//                                                 '(No reviews)'
//                                             )}
//                                         </span>
//                                     </div>

//                                     {/* Price Section */}
//                                     <div className='flex items-center gap-2 flex-wrap mt-2'>
//                                         <span 
//                                             className='font-normal text-lg text-slate-800'
//                                             style={{ fontFamily: 'Georgia, serif', fontWeight: 400 }}
//                                         >
//                                             {displayNGNCurrency(product.productPrice)}
//                                         </span>
//                                         {product.productDiscount && (
//                                             <span 
//                                                 className='text-xs line-through text-neutral-500'
//                                                 style={{ fontFamily: 'Georgia, serif' }}
//                                             >
//                                                 {displayNGNCurrency(product.productDiscount)}
//                                             </span>
//                                         )}
//                                     </div>

//                                     {/* Add to Cart Button */}
//                                     <button
//                                         onClick={(e) => handleAddToCart(e, product?._id)}
//                                         className='w-full py-2.5 px-4 font-medium text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-md flex items-center justify-center gap-2 bg-slate-800 text-xs tracking-wider uppercase'
//                                         style={{ fontFamily: 'Georgia, serif' }}
//                                     >
//                                         <ShoppingCart size={15} />
//                                         Add to cart
//                                     </button>
//                                 </div>
//                             </Link>
//                         </div>
//                     ))
//                 )}
//             </div>

//             <style jsx>{`
//                 .scrollbar-none::-webkit-scrollbar {
//                     display: none;
//                 }
//                 .scrollbar-none {
//                     -ms-overflow-style: none;
//                     scrollbar-width: none;
//                 }
//             `}</style>
//         </div>
//     )
// }

// export default CategoryProductDisplay;